/**
 * Histórico — os atendimentos encerrados, com filtro e exportação.
 *
 * ## A tela NÃO EXISTE sem a permissão, e isso é a exceção
 *
 * §5.9 da spec 05: *"sem `read_conversation_history` a tela não existe — nem vazia, nem desabilitada. É
 * o único lugar desta spec onde 'esconder' é a resposta certa"*. Em todo o resto do produto a regra é a
 * oposta: controle desabilitado **com o motivo**, porque sumir com ele faz o admin achar que o produto
 * não tem a função.
 *
 * O que muda aqui: um índice pesquisável de meses de conversa alheia não é uma função cuja existência
 * precise ser anunciada a quem não pode usá-la. A barra de áreas respeita isso pela `permissao` da área.
 *
 * ## Espera e atendimento em colunas separadas
 *
 * A divergência mais útil da ADR-0004, e nesta tela ela é literal: **duas colunas**. O concorrente tem
 * uma `Duração` que soma as duas e mostra `16:16` para um atendimento de três minutos que passou a noite
 * na fila.
 *
 * ## A forma (spec 22, 31/10)
 *
 * Família CONSULTA: `PaginaInteira` + `ListView` — o cabeçalho da lista é o da tela, a tabela rola por dentro com
 * o cabeçalho das colunas preso. A busca e os filtros continuam indo ao SERVIDOR (o CSV leva os mesmos), por isso
 * moram nas ações da lista, e não na busca local do kit.
 */

import { useState } from "react";
import { contar, falhaDe, useSessao } from "@hugi/ui";
import { Dica, ListView, PaginaInteira, Selo, Vazio, type ColunaDaLista } from "@hugi/ui/kit";
import { Download, History, Search } from "lucide-react";
import { duracaoCurta, useCatalogos, useHistorico, type LinhaDoHistorico } from "./dados.js";
import "./hugi-historico.css";

const PERIODOS = [
  { dias: 7, rotulo: "7 dias" },
  { dias: 30, rotulo: "30 dias" },
  { dias: 90, rotulo: "90 dias" },
] as const;

const COLUNAS: ColunaDaLista<LinhaDoHistorico>[] = [
  { id: "protocolo", rotulo: "Protocolo", celula: (i) => <span className="hugi-historico-mono">{i.protocolo}</span>, ordenar: (i) => i.protocolo },
  {
    id: "contato",
    rotulo: "Contato",
    principal: true,
    quebra: true,
    ordenar: (i) => i.contato,
    celula: (i) => (
      <span className="hugi-historico-contato">
        <span className="hugi-historico-nome">{i.contato}</span>
        <span className="hugi-historico-identificador">
          <span className="hugi-historico-mono">{i.identificador}</span>
          {i.etiquetas.map((e) => (
            <span key={e} className="hugi-historico-etiqueta">{e}</span>
          ))}
        </span>
      </span>
    ),
  },
  { id: "fila", rotulo: "Fila", celula: (i) => i.fila ?? "—", ordenar: (i) => i.fila },
  // Atendente nulo significa que quem atendeu foi o AGENTE. Dizer isso é melhor que um traço.
  { id: "atendente", rotulo: "Atendente", celula: (i) => i.atendenteId ?? "agente", ordenar: (i) => i.atendenteId ?? "agente" },
  {
    id: "motivo",
    rotulo: "Motivo",
    quebra: true,
    ordenar: (i) => i.motivo,
    celula: (i) => (
      <span className="hugi-historico-motivo">
        {i.motivo ?? "—"}
        {/* Automático fica MARCADO: encerramento por inatividade não é alguém decidindo que resolveu. */}
        {i.motivoAutomatico ? <Selo tom="neutro">automático</Selo> : null}
      </span>
    ),
  },
  { id: "espera", rotulo: "Espera", numerica: true, celula: (i) => duracaoCurta(i.esperaSegundos), ordenar: (i) => i.esperaSegundos },
  { id: "atendimento", rotulo: "Atendimento", numerica: true, celula: (i) => duracaoCurta(i.atendimentoSegundos), ordenar: (i) => i.atendimentoSegundos },
  {
    id: "encerrado",
    rotulo: "Encerrado",
    ordenar: (i) => i.finalizadoEm,
    celula: (i) => (
      <span className="hugi-historico-encerrado">
        {i.finalizadoEm ? new Date(i.finalizadoEm).toLocaleString("pt-BR") : "—"}
        {i.conteudoExpurgado ? <span className="hugi-historico-expurgado">conteúdo expurgado</span> : null}
      </span>
    ),
  },
];

export function Historico() {
  const sessao = useSessao();
  const [dias, setDias] = useState(30);
  const [busca, setBusca] = useState("");
  const [filaId, setFilaId] = useState("");
  const [motivoId, setMotivoId] = useState("");
  const catalogos = useCatalogos();
  const historico = useHistorico({ dias, busca, filaId, motivoId });

  /**
   * Sem a permissão, a tela some.
   *
   * Não é `SemPermissao` com o nome da permissão, como nas outras: aqui nomear a função seria
   * anunciá-la. A pessoa vê o produto sem esta área, e é isso.
   */
  if (!sessao.permissoes.includes("read_conversation_history")) {
    return (
      <PaginaInteira rotulo="Histórico">
        <Vazio>Esta área não está disponível na sua conta.</Vazio>
      </PaginaInteira>
    );
  }

  /** A URL do CSV carrega os MESMOS filtros da tela — exportar o que se vê, e não a base inteira. */
  const urlDoCsv = (() => {
    const q = new URLSearchParams({ dias: String(dias) });
    if (busca.trim()) q.set("busca", busca.trim());
    if (filaId) q.set("fila", filaId);
    if (motivoId) q.set("motivo", motivoId);
    return `/api/painel/historico.csv?${q}`;
  })();

  const itens = historico.data?.itens;
  const algumExpurgado = (itens ?? []).some((i) => i.conteudoExpurgado);
  const falha = historico.error ? falhaDe(historico.error) : null;

  const filtros = (
    <>
      <Dica texto="Atendimentos encerrados. Espera e atendimento aparecem separados — somados não descrevem nada." />
      <label className="v2-lista-busca">
        <span className="v2-sr">Buscar por nome, número ou protocolo</span>
        <Search size={14} aria-hidden="true" />
        <input
          type="search"
          aria-label="Buscar por nome, número ou protocolo"
          placeholder="Nome, número ou protocolo"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </label>

      <select aria-label="Fila" value={filaId} onChange={(e) => setFilaId(e.target.value)} className="hugi-historico-filtro">
        <option value="">Todas as filas</option>
        {(catalogos.data?.filas ?? []).map((f) => (
          <option key={f.id} value={f.id}>
            {f.nome}
          </option>
        ))}
      </select>

      <select aria-label="Motivo de encerramento" value={motivoId} onChange={(e) => setMotivoId(e.target.value)} className="hugi-historico-filtro">
        <option value="">Todos os motivos</option>
        {(catalogos.data?.motivos ?? []).map((m) => (
          <option key={m.id} value={m.id}>
            {m.nome}
          </option>
        ))}
      </select>

      <span role="group" aria-label="Período" className="hugi-historico-periodo">
        {PERIODOS.map((p) => (
          <button key={p.dias} type="button" onClick={() => setDias(p.dias)} aria-pressed={dias === p.dias}>
            {p.rotulo}
          </button>
        ))}
      </span>

      <a href={urlDoCsv} download className="v2-botao">
        <Download size={14} aria-hidden="true" /> Exportar CSV
      </a>
    </>
  );

  return (
    <PaginaInteira rotulo="Histórico" classe="hugi-historico">
      <ListView
        titulo="Histórico"
        icone={<History size={16} />}
        colunas={COLUNAS}
        linhas={itens}
        chave={(i) => i.id}
        estado={{
          carregando: historico.isPending,
          erro: Boolean(historico.error),
          ...(falha?.erro.mensagem ? { mensagemDeErro: falha.erro.mensagem } : {}),
          semPermissao: falha?.tipo === "sem_permissao",
          aoTentarDeNovo: () => void historico.refetch(),
        }}
        acoes={filtros}
        /*
          Dizer quantos apareceram e que há um teto. Uma lista truncada em silêncio é o que faz alguém concluir
          que o mês teve 100 atendimentos.
        */
        contagem={(n) => `${contar(n, "atendimento", "atendimentos")} · a tela mostra até 100, a exportação leva até 5.000`}
        avisos={
          /*
            O aviso de retenção fica no cabeçalho, e não numa nota de pé: quem vai exportar precisa saber disso
            antes de mandar o arquivo para outra pessoa.
          */
          algumExpurgado ? (
            <span role="note" className="hugi-historico-retencao">
              {" · "}parte já passou pela retenção
              <Dica texto="O texto das mensagens foi apagado e o registro do atendimento permanece. A exportação diz isso linha por linha, em vez de entregar coluna vazia." />
            </span>
          ) : null
        }
        vazio={{
          semNada: (
            <>
              Nenhum atendimento encerrado nesse período.
              <Dica texto="Ajuste o período ou limpe os filtros. Atendimento entra aqui quando alguém o finaliza com um motivo." />
            </>
          ),
        }}
      />
    </PaginaInteira>
  );
}
